import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Miscellanious } from '@ops/global-config';
import {
  OpsSubscriptionModalService,
  PricingPlanMapped,
  RecurringIntervalPrice,
  StripeService,
  StripeStatus,
  SubscriptionStatus,
} from '@ops/shared-types';
import { MappedAddOn } from 'legacy/shared/shared-types/src/lib/models/stripe-product-addons.model';
import { LocalStorageService } from 'legacy/shared/shared-types/src/lib/services/storage.service';
import { Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ops-subscribed-card',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './ops-subscribed-card.component.html',
  styleUrl: './ops-subscribed-card.component.scss',
})
export class OpsSubscribedCardComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() plan: PricingPlanMapped;
  @Input() subscriptionData: any;
  @Input() subscriptionId: string;
  @Input() isSelected?: boolean;
  @Input() isPreview?: boolean;
  @Input() isDisabled?: boolean;
  @Input() selectedCurrency: string = 'USD';

  @Input() addOns: MappedAddOn[] = [];

  @Output() selectPlan?: EventEmitter<string> = new EventEmitter<string>();
  @Output() changePlan?: EventEmitter<void> = new EventEmitter<void>();
  @Output() cancelSubscription?: EventEmitter<void> = new EventEmitter<void>();
  @Output() resumeSubscription?: EventEmitter<void> = new EventEmitter<void>();

  @Output() addOnUpdates?: EventEmitter<any> = new EventEmitter<any>();

  @Input() showMonthly: boolean = true;

  extraUsers: number = 0;
  extraProjects: number = 0;

  isLoading: boolean = false;

  confirmSubscription: Subscription;

  constructor(
    private stripeService: StripeService,
    private modalService: OpsSubscriptionModalService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.addOns.map((addOnItem) => {
      return {
        ...addOnItem,
        showPrice: this.showMonthly ? addOnItem.monthly : addOnItem.yearly,
      };
    });

    if (
      this.subscriptionData?.limitations?.maxUserPerCompany &&
      this.plan?.limitations?.maxUserPerCompany
    ) {
      this.extraUsers =
        this.subscriptionData.limitations.maxUserPerCompany -
        this.plan.limitations.maxUserPerCompany;
    }
    
    // Refactored extraProjects calculation using storage service
    this.calculateExtraProjects();

    // console.log("HERE HERE HERE \n\n", this.subscriptionData);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.subscriptionData?.limitations?.maxUserPerCompany &&
      this.plan?.limitations?.maxUserPerCompany
    ) {
      this.extraUsers =
        this.subscriptionData.limitations.maxUserPerCompany -
        this.plan.limitations.maxUserPerCompany;
    }
    
    // Refactored extraProjects calculation using storage service
    this.calculateExtraProjects();
  }

  private calculateExtraProjects(): void {
    if (
      this.subscriptionData?.limitations?.maxRealProjectsInCompany &&
      this.subscriptionData?.pricingPlanMapped?.stripeProductId
    ) {
      // Get all products from storage service
      const allProducts = this.localStorageService.getAllStripeProducts();
      
      // Find the matching product by stripeProductId
      const matchingProduct = allProducts.find(
        (product: any) => product.stripeProductId === this.subscriptionData.pricingPlanMapped.stripeProductId
      );
      
      if (matchingProduct?.limitations?.maxRealProjectsInCompany) {
        // Calculate difference using default maxRealProjectsInCompany from storage
        this.extraProjects =
          this.subscriptionData.limitations.maxRealProjectsInCompany -
          matchingProduct.limitations.maxRealProjectsInCompany;
      } else {
        // Fallback to original calculation if matching product not found
        if (this.plan?.limitations?.maxRealProjectsInCompany) {
          this.extraProjects =
            this.subscriptionData.limitations.maxRealProjectsInCompany -
            this.plan.limitations.maxRealProjectsInCompany;
        }
      }
    }
  }

  get priceForMonth(): RecurringIntervalPrice {
    return this.plan?.monthly[this.selectedCurrency];
  }

  get priceForYear(): RecurringIntervalPrice {
    return this.plan?.yearly[this.selectedCurrency];
  }

  get currencyExchangeSymbol() {
    return Miscellanious.currencySymbolFromCode(
      'en-US',
      this.priceForYear?.currency || this.selectedCurrency || 'USD'
    );
  }

  get isCanceledSubscription() {
    return this.subscriptionData.status === 'canceled' || this.subscriptionData.cancellationRequested;
  }

  getExtraCount(addOn: MappedAddOn) {
    // if (addOn.name === 'Add extra user') {
    //   return this.extraUsers;
    // }

    if (addOn.name === 'Add extra project') {
      return this.extraProjects;
    }
  }

  hasExtraItem(addOn: MappedAddOn) {
    // if (addOn.name === 'Add extra user') {
    //   return !!this.extraUsers;
    // }

    if (addOn.name === 'Add extra project') {
      return !!this.extraProjects;
    }
  }

  onConfirmedAddExtraItem(addOn: MappedAddOn) {
    // console.log('PLAN => ', this.plan, this.subscriptionId);
    const priceId = this.showMonthly
      ? addOn.monthly[this.selectedCurrency]?.id
      : addOn.yearly[this.selectedCurrency]?.id;
    this.isLoading = true;
    this.stripeService
      .addAddOnOnSubscriptionId(this.subscriptionId, priceId)
      .subscribe(
        (resp: any) => {
          const respJson = resp ? JSON.parse(resp || '{}') : {};
          // console.log('RESP ADD', resp);
          this.addOnUpdates.emit(respJson);
          this.isLoading = false;
          // if(respJson?.maxRealProjectsInCompany){
          //   this.extraUsers = respJson.maxUserPerCompany - this.plan.limitations.maxUserPerCompany;
          //   this.extraProjects = respJson.maxRealProjectsInCompany - this.plan.limitations.maxRealProjectsInCompany;
          // }
        },
        (errorResp) => {
          this.isLoading = false;
        }
      );
  }

  onAddExtraItem(addOn: MappedAddOn) {
    const titleText = this.translateService.instant('opsSubscribedCard.confirm');

    const addText = this.translateService.instant('opsSubscribedCard.addExtraProject');
    const preText = this.translateService.instant('opsSubscribedCard.areYouSureYouWantTo');
    const confirmationText = `${preText} ${addText}`;
    this.showConfirmationModal(
      titleText,
      confirmationText,
      this.onConfirmedAddExtraItem.bind(this, addOn)
    );
  }

  onConfirmedRemoveExtraItem(addOn: MappedAddOn) {
    const priceId = this.showMonthly
      ? addOn.monthly[this.selectedCurrency]?.id
      : addOn.yearly[this.selectedCurrency]?.id;
    this.isLoading = true;
    this.stripeService
      .removeAddOnFromSubscriptionId(this.subscriptionId, priceId)
      .subscribe({
        next: (resp: any) => {
          const respJson = resp ? JSON.parse(resp || '{}') : {};
          this.addOnUpdates.emit(respJson); 
          this.isLoading = false;
        },
        error: (errorResp) => {
          const respJson = errorResp?.error
            ? JSON.parse(errorResp.error || '{}')
            : {};
          if (
            respJson.error.message === this.translateService.instant('opsSubscribedCard.tooManyUsersError')
          ) {
            this.showErrorModal(
              this.translateService.instant('opsSubscribedCard.removeAddOnUsersError')
            );
          } else {
            this.showErrorModal(
              this.translateService.instant('opsSubscribedCard.removeAddOnProjectsError')
            );
          }
          // console.log('error', respJson.error.message);
          this.isLoading = false;
        },
      });
  }

  onRemoveExtraItem(addOn: MappedAddOn) {
    const trimmedAddOn = addOn.name.toLowerCase().replace('add', 'remove');
    
    const removeText = this.translateService.instant('opsSubscribedCard.removeExtraProject');
    const preText = this.translateService.instant('opsSubscribedCard.areYouSureYouWantTo');

    const titleText = this.translateService.instant('opsSubscribedCard.confirm');

    const confirmationText = `${preText} ${removeText}`;
    this.showConfirmationModal(
      titleText,
      confirmationText,
      this.onConfirmedRemoveExtraItem.bind(this, addOn)
    );
  }

  ngOnDestroy(): void {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
  }

  onChangeCurrentPlan() {
    // if (this.subscriptionData.status === StripeStatus.TRIALING) {
    //   this.showErrorModal(
    //     'Plan changes aren't available during the trial period. You can either cancel your current plan and subscribe to a new one, or wait for the trial to end before making changes.'
    //   );
    // } else {
    this.changePlan.emit();
    // }
  }

  onSelectPlan() {
    // this.selectPlan.emit(this.plan.id);
  }

  onCancelSubscription() {
    this.cancelSubscription.emit();
  }

  onResumeSubscription() {
    this.resumeSubscription.emit();
  }

  showSuccessModal() {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    this.confirmSubscription = this.modalService
      .openModal({
        statusType: SubscriptionStatus.SUCCESS,
        modalText: this.translateService.instant('opsSubscribedCard.subscriptionSuccessful'),
      })
      .subscribe((confirmed) => {
        if (confirmed !== null) {
          // Handle confirmation (only relevant for confirmation dialogs)
          // console.log('User confirmed:', confirmed);
        }
      });
  }

  showErrorModal(customText: string) {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    this.modalService.openModal({
      statusType: SubscriptionStatus.ERROR,
      modalText: customText,
    });
  }

  showConfirmationModal(
    titleText: string,
    bodyText: string,
    confirmationCallBack: () => void
  ) {
    if (this.confirmSubscription?.unsubscribe) {
      this.confirmSubscription.unsubscribe();
    }
    this.confirmSubscription = this.modalService
      .openModal({
        statusType: SubscriptionStatus.CONFIRMATION,
        modalTitle: titleText,
        modalText: bodyText,
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          // User confirmed
          // console.log('User confirmed cancellation');
          confirmationCallBack();
        } else {
          // User declined
          // console.log('User cancelled the action');
        }
      });
  }
}
